import { useState } from 'react';
import { Box, Btn, Txt } from '../ui/primitives';
import { groupStyle } from '../theme/statusTheme';
import { num, percent } from '../utils/format';
import type { DistributionSlice } from '../api/types';

/**
 * Order status distribution.
 *
 * FORM: horizontal bars ordered by lifecycle stage (not by size), so the chart
 * reads as the journey an order takes. Nine statuses with long names is exactly
 * the case where horizontal bars beat a donut: each label sits on its own row
 * and never collides, and small counts stay visible instead of collapsing into
 * unreadable slivers.
 *
 * COLOUR: assigned by lifecycle group, never by rank, so filtering or a quiet
 * day never repaints the surviving bars. Every bar carries its status name and
 * count as visible text, so colour reinforces meaning and never carries it
 * alone. That direct labelling is also the required relief for the amber step's
 * sub-3:1 contrast against the light surface.
 *
 * INTERACTION: hover (or keyboard focus) dims the other bars and reveals the
 * hovered bar's share of the day; activating a bar filters the orders table.
 */
export interface StatusDistributionProps {
  distribution: DistributionSlice[];
  total: number;
  activeStatuses: readonly string[];
  onSelectStatus: (status: string) => void;
}

export function StatusDistribution({
  distribution,
  total,
  activeStatuses,
  onSelectStatus,
}: StatusDistributionProps): JSX.Element {
  const [hovered, setHovered] = useState<string | null>(null);

  // Zero-count statuses are dropped so the chart shows the day that happened
  // rather than the schema.
  const visible = distribution.filter((d) => d.count > 0);
  const max = Math.max(1, ...visible.map((d) => d.count));

  return (
    <Box
      as="section"
      aria-label="Order status distribution"
      className="rounded-card bg-surface-card ring-1 ring-hairline shadow-card p-5 sm:p-6 flex flex-col"
    >
      <Box className="flex items-baseline justify-between gap-3">
        <Txt as="h2" className="text-[15px] font-semibold text-ink-primary">
          Order Status Distribution
        </Txt>
        <Txt className="text-[12px] text-ink-muted tabular">{num(total)} orders</Txt>
      </Box>

      {visible.length === 0 ? (
        <Txt as="p" className="mt-6 text-[13px] text-ink-muted">
          No orders to distribute for this period.
        </Txt>
      ) : (
        <>
          <LifecycleBar slices={visible} total={total} />

          <Box className="mt-5 flex flex-col gap-2">
          {visible.map((slice) => {
            const style = groupStyle(slice.group);
            const widthPct = (slice.count / max) * 100;
            const selected = activeStatuses.includes(slice.status);
            const isHovered = hovered === slice.status;
            const dimmed = hovered !== null && !isHovered;
            const sharePct = percent((slice.count / Math.max(1, total)) * 100);

            return (
              <Btn
                key={slice.status}
                onPress={() => onSelectStatus(slice.status)}
                onHoverIn={() => setHovered(slice.status)}
                onHoverOut={() => setHovered(null)}
                aria-pressed={selected}
                aria-label={`${slice.label}: ${slice.count} orders, ${sharePct} of the day. Activate to filter the table by this status.`}
                className={[
                  'w-full text-left rounded-lg px-2 py-1.5 -mx-2 transition-colors duration-150',
                  selected ? 'bg-surface-sunken ring-1 ring-hairline' : 'hover:bg-surface-sunken',
                ].join(' ')}
              >
                <Box className="flex items-center justify-between gap-3">
                  <Box className="flex items-center gap-2 min-w-0">
                    <Box
                      className="w-2.5 h-2.5 rounded-[3px] shrink-0"
                      style={{ backgroundColor: style.fill }}
                      aria-hidden
                    />
                    <Txt className="text-[13px] font-medium text-ink-primary truncate">{slice.label}</Txt>
                  </Box>
                  <Box className="flex items-baseline gap-2 shrink-0">
                    <Txt
                      className="text-[12px] text-ink-muted tabular transition-opacity duration-150"
                      style={{ opacity: isHovered || selected ? 1 : 0 }}
                    >
                      {sharePct}
                    </Txt>
                    <Txt className="text-[14px] font-semibold text-ink-primary tabular">{num(slice.count)}</Txt>
                  </Box>
                </Box>

                {/* Track + fill: thin mark, rounded data-end, anchored left. */}
                <Box
                  className="mt-1.5 h-2 w-full rounded-full overflow-hidden"
                  style={{ backgroundColor: 'var(--surface-sunken)' }}
                >
                  <Box
                    className="h-full rounded-full transition-[width,opacity] duration-500 ease-out"
                    style={{
                      width: `${Math.max(widthPct, 1.5)}%`,
                      backgroundColor: style.fill,
                      opacity: dimmed ? 0.4 : 1,
                    }}
                  />
                </Box>
              </Btn>
            );
          })}
          </Box>
        </>
      )}

      <Box className="mt-5 pt-4 border-t border-hairline">
        <Txt as="p" className="text-[11px] text-ink-muted">
          Bars follow the order lifecycle. Select one to filter the orders table.
        </Txt>
      </Box>
    </Box>
  );
}

/**
 * Part-to-whole summary: one stacked bar showing how the day divides across the
 * lifecycle, with a 2px surface gap between segments so adjacent fills stay
 * readable. Segments follow lifecycle order, matching the bars below.
 */
function LifecycleBar({ slices, total }: { slices: DistributionSlice[]; total: number }): JSX.Element {
  const safeTotal = Math.max(1, total);

  return (
    <Box className="mt-4">
      <Box className="flex items-stretch gap-[2px] h-3 w-full">
        {slices.map((slice) => {
          const style = groupStyle(slice.group);
          return (
            <Box
              key={slice.status}
              className="first:rounded-l-full last:rounded-r-full transition-[flex-grow] duration-500 ease-out"
              style={{
                flexGrow: slice.count,
                flexBasis: 0,
                minWidth: 3,
                backgroundColor: style.fill,
              }}
              title={`${slice.label}: ${num(slice.count)}`}
            />
          );
        })}
      </Box>

      <Box className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {slices.map((slice) => {
          const style = groupStyle(slice.group);
          return (
            <Box key={slice.status} className="flex items-center gap-1.5">
              <Box className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: style.fill }} aria-hidden />
              <Txt className="text-[11px] text-ink-muted">
                {slice.label}
                <Txt className="text-ink-secondary font-medium tabular">
                  {' '}
                  {percent((slice.count / safeTotal) * 100, 0)}
                </Txt>
              </Txt>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
